import { AuthState } from '@/context/AuthContext';
import { TypeLink_internalFields } from '@/core/generated-types/TypeLink_internal';
import { format } from 'light-date';
import { noticeError } from '@/utils/newrelicHelpers';

declare global {
  interface Window {
    rudderanalytics: Record<string, any>;
  }
}

interface IdentifyUserTraits {
  email?: string;
  firstName?: string;
  childBirthDate?: Date;
}

interface TrackCodeAppliedParams {
  type?: string;
  code: string;
  successfullyApplied: boolean;
  message: string;
  discountValue?: number | string;
  additionalOpts?: object;
}

interface TrackCodeRemovedParams {
  type?: string;
  code: string;
  discountValue?: number | string;
  additionalOpts?: object;
}

export enum CHECKOUT_EVENT_TYPES {
  CHECKOUT_CODE_APPLIED = 'Checkout Code Applied',
  CHECKOUT_CODE_REMOVED = 'Checkout Code Removed',
  ERROR_VIEWED = 'Error Viewed',
}

export enum SESSION_EVENT_TYPES {
  SESSION_STARTED = 'Session Started',
}

export enum PRODUCT_EVENT_TYPES {
  PRODUCT_VIEWED = 'Product Viewed',
  PRODUCT_CLICKED = 'Product Clicked',
  PRODUCT_ADDED = 'Product Added',
  PRODUCT_REMOVED = 'Product Removed',
}

export enum ITEM_EVENT_TYPES {
  ITEM_CLICKED = 'Item Clicked',
  BANNER_VIEWED = 'Banner Viewed',
  BANNER_CLICKED = 'Banner Clicked',
  BANNER_DISMISSED = 'Banner Dismissed',
}

export enum EXTENSION_UPSELL_EVENT_TYPES {
  EXTENSION_FLOW_STEP_VIEWED = 'Extension Flow Step Viewed',
  EXTENSION_FLOW_STEP_COMPLETED = 'Extension Flow Step Completed',
}

export const EXTENSION_UPSELL_EVENT_STEPS = {
  VIEWED: { name: 'Upsell Options', stepNumber: '2' },
  COMPLETED: { name: 'Extension Upsell', stepNumber: '' },
};

export enum CLICKED_ITEM_TYPES {
  LINK = 'Link',
  DISMISS = 'Dismiss',
  VIEW = 'View',
  BUTTON = 'Button',
}

export enum PAGE_LOCATIONS {
  COURSE_PACKS_TILE = 'Course Packs tile',
  PLAY_KITS_TILE = 'Play Kits tile',
  SUBSCRIBER_SHOP_TILE = 'Subscriber Shop tile',
  ORDER_HISTORY_TILE = 'Order History tile',
  READING_SKILL_SET = 'Reading Skill Set tile',
  REFER_A_FRIEND_TILE = 'Refer A Friend tile',
  ACCOUNT_SETTINGS_TILE = 'Account Settings tile',
  MANAGE_SUBSCRIPTION_TILE = 'Manage Subscription tile',
  SITE_BANNER = 'Site Banner',
  PLAY_KITS_AGE_BASED = 'Explore The Play Kits by Age Module',
  NAVIGATION = 'Navigation',
  PLAY_KITS_PAGE = 'Play Kits Page',
  HOMEPAGE = 'Homepage',
  CHAT_WIDGET = 'Chat Widget',
  CUSTOMER_INFO = 'Customer Info',
  CHECKOUT = 'Checkout',
}

export enum CTA_NAMES {
  VIEW = 'View',
  SKIP_KIT = 'Skip Kit',
  CANCEL_SUBSCRIPTION = 'Cancel Subscription',
  REMOVE_BOOK_BUNDLE = 'Remove Book Bundle',
  PREPAY_AND_SAVE_NOW = 'Prepay & Save Now',
  REACTIVATE_SUBSCRIPTION = 'Reactivate Subscription',
  SUBSCRIBE_NOW = 'Subscribe Now',
  ENABLE_ACCESSIBILITY = 'Enable Accessibility',
  EXIT_ACCESSIBILITY = 'Exit Accessibility Mode',
  ZERO_12_MONTHS = '0-12 Months',
  ONE_YEAR = '1-Year-Old',
  TWO_YEAR = '2-Year-Old',
  THREE_YEAR = '3-Year-Old',
  FOUR_YEAR = '4-Year-Old',
  CHILD_SELECTED = 'Child Selected',
  ADD_CHILD = 'Add Child',
  EDIT_CHILD = 'Edit Child',
  PICK_UP_WHERE_YOU_LEFT_OFF = 'Pick up where you left off',
  CHAT = 'Chat',
  EDIT = 'Edit',
  RETURN_TO_CART = 'Return to Cart',
  EDIT_SHIP_DATE = 'Edit Ship Date',
  EDIT_SHIPPING_ADDRESS = 'Edit Shipping Address',
  REPLACEMENT_PARTS = 'Replacement Parts',
  ADD_BOOKS_ADDON = 'Add Books Addon',
  MANAGE_PAYMENT_METHOD = 'Manage Payment Method',
  EXPLORE_THE_PLAY_KITS = 'Explore The Play Kits',
  PREPAY_EXTENSION_FLOW_STARTED = 'Prepay Extension Flow Started',
  GIFT_EXTENSION_FLOW_STARTED = 'Gift Extension Flow Started',
}

interface ProductEventPayload {
  url: string;
  productId: string;
  sku: string;
  category: string;
  name: string;
  ageRange?: string;
  ageUnits: string;
  price: number;
  quantity: number;
}

interface FormErrorEventPayload {
  errorType: string;
  errors: string[];
  url: string;
}

interface ItemClickedEventPayload {
  url: string;
  type: CLICKED_ITEM_TYPES | ITEM_EVENT_TYPES;
  pageLocation: PAGE_LOCATIONS;
  cta: CTA_NAMES;
}

interface ExtensionFlowStepEventPayload {
  stepName: string;
  url: string;
  bundleSize: string;
  platform: string;
  stepNumber?: string;
  subtotal?: string;
  discountValue?: string;
  products?: string[];
}

const LE_REPO = 'lovevery-next';

function defaultTrackPayload() {
  return {
    le_repo: LE_REPO,
    lovevery_region: process.env.NEXT_PUBLIC_REGION,
  };
}

export function track(event: string, properties = {}) {
  window.rudderanalytics?.track(event, {
    ...defaultTrackPayload(),
    ...properties,
  });
}

export function pageEvent(category?: string) {
  window.rudderanalytics?.page(category, defaultTrackPayload());
}

export function trackProductEvent(
  eventName: PRODUCT_EVENT_TYPES,
  payload: ProductEventPayload,
) {
  track(eventName, payload);
}

export function trackExtensionFlowStepEvent(
  payload: ExtensionFlowStepEventPayload,
) {
  if (payload.stepName === EXTENSION_UPSELL_EVENT_STEPS.COMPLETED.name) {
    track(EXTENSION_UPSELL_EVENT_TYPES.EXTENSION_FLOW_STEP_COMPLETED, payload);
  } else {
    track(EXTENSION_UPSELL_EVENT_TYPES.EXTENSION_FLOW_STEP_VIEWED, payload);
  }
}

export function trackItemClickedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.ITEM_CLICKED, payload);
}

export function trackBannerViewedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.BANNER_VIEWED, payload);
}

export function trackBannerClickedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.BANNER_CLICKED, payload);
}

export function trackFormErrorEvent(payload: FormErrorEventPayload) {
  track(CHECKOUT_EVENT_TYPES.ERROR_VIEWED, payload);
}

export function trackBannerDismissedEvent(payload: ItemClickedEventPayload) {
  track(ITEM_EVENT_TYPES.BANNER_DISMISSED, payload);
}

export function trackCodeApplied(payload: TrackCodeAppliedParams) {
  track(CHECKOUT_EVENT_TYPES.CHECKOUT_CODE_APPLIED, payload);
}
export function trackCodeRemoved(payload: TrackCodeRemovedParams) {
  track(CHECKOUT_EVENT_TYPES.CHECKOUT_CODE_REMOVED, payload);
}

export function buildPayloadUrl(fields: TypeLink_internalFields) {
  const internalLinkSlug = fields?.linkContent?.fields?.slug || '';
  return window.location.href + internalLinkSlug;
}

export function cleanPayload(payload: Record<string, any>) {
  Object.keys(payload).forEach((key) => {
    if (
      payload[key] === null ||
      payload[key] === undefined ||
      payload[key] === ''
    ) {
      delete payload[key];
    }
  });

  return payload;
}

// Retry the identify call until the rudderanalytics object is available.
// TODO: Calling this userUuid - what is the difference between these two?
// user.attributes['custom:account_manager_id'] || user.attributes.loveveryId,
// Not awaiting this async because it could never clear
// Wait up to 5 seconds for the rudderanalytics object to be available
// IdentifyUserTraits has childBirthDate as a Date, but the API expects a string
//
export function queueIdentifyWithSessionStart(
  userUuid: string,
  traits: IdentifyUserTraits,
  retry = 0,
) {
  if (window.rudderanalytics) {
    const payload = cleanPayload({
      ...traits,
      childBirthDate: _extractDateYearAndMonth(traits.childBirthDate),
    });
    window.rudderanalytics.identify(
      userUuid,
      payload,
      {},
      (cbResponse: any) => {
        const started = cbResponse?.message?.context?.sessionStart;
        if (started) {
          track(SESSION_EVENT_TYPES.SESSION_STARTED);
        }
      },
    );
  } else if (retry < 20) {
    setTimeout(() => {
      queueIdentifyWithSessionStart(userUuid, traits, retry + 1);
    }, 250);
  }
}

export async function identifyCurrentUser(
  user: AuthState | null,
  userTraits: IdentifyUserTraits = {},
) {
  if (!user || !user.attributes) {
    noticeError('icua not present');
    return;
  }

  if (!user.attributes['custom:account_manager_id']) {
    noticeError('icuami not present');
    return;
  }

  queueIdentifyWithSessionStart(
    user.attributes['custom:account_manager_id'],
    cleanPayload({
      email: user.attributes.email,
      firstName: user.attributes.name,
      ...userTraits,
    }),
  );
}

export function _extractDateYearAndMonth(
  date: Date | undefined,
): string | null {
  if (typeof date === 'undefined') return null;

  return format(date, '{yyyy}-{MM}');
}
